// ============================================================
// Full path:
//   D:\Thyroid Consultation Software\ThyroConsult Frontend\thyroconsult-frontend\src\components\Turnstile.js
//
// Cloudflare Turnstile — the "I'm not a robot" check (1 Oct 2026).
// Usually it passes by itself without the person doing anything; now and
// then it shows a single tick box. The server checks the token
// (backend middleware/turnstile.js) only once TURNSTILE_SECRET_KEY is set
// there, so before that the box is shown but not required.
//
// Usage:
//   const ts = useRef(null); const [tsToken, setTsToken] = useState('');
//   <Turnstile ref={ts} onToken={setTsToken} />
// Optional (7 Oct 2026):
//   appearance — 'interaction-only' (default: hidden unless a tick is
//                needed) or 'always' (the box is always visible).
//   onError(code) — the widget could not run (e.g. the website's address
//                is not on the Turnstile site's list of allowed domains,
//                or the script is blocked). code = Cloudflare's error code.
//   …send { ..., turnstileToken: tsToken }, then ts.current.reset()
//   (each token works once, so reset after every request).
// ============================================================
import React, { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';

// Public site key (safe to be in the website). REACT_APP_TURNSTILE_SITE_KEY
// overrides it if ever needed.
export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAFLtPBFlqv7LgE_E';

let scriptPromise = null;
function loadScript() {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true; s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => { scriptPromise = null; reject(new Error('Turnstile script failed to load')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

const Turnstile = forwardRef(function Turnstile({ onToken, onError, action, appearance, style }, ref) {
  const box = useRef(null);
  const widgetId = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useImperativeHandle(ref, () => ({
    reset: () => {
      onTokenRef.current && onTokenRef.current('');
      if (window.turnstile && widgetId.current !== null) {
        try { window.turnstile.reset(widgetId.current); } catch (_) { /* widget gone */ }
      }
    },
  }));

  useEffect(() => {
    let cancelled = false;
    if (!TURNSTILE_SITE_KEY) return undefined;
    loadScript().then(() => {
      if (cancelled || !box.current || !window.turnstile) return;
      widgetId.current = window.turnstile.render(box.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action: action || undefined,
        appearance: appearance || 'interaction-only',   // default: shows only when a tick is needed
        callback: (token) => onTokenRef.current && onTokenRef.current(token),
        'expired-callback': () => onTokenRef.current && onTokenRef.current(''),
        'error-callback': (code) => {
          onTokenRef.current && onTokenRef.current('');
          onErrorRef.current && onErrorRef.current(code || 'error');
        },
      });
    }).catch(() => {
      // blocked or offline — the server decides
      onErrorRef.current && onErrorRef.current('script_blocked');
    });
    return () => {
      cancelled = true;
      if (window.turnstile && widgetId.current !== null) {
        try { window.turnstile.remove(widgetId.current); } catch (_) { /* already gone */ }
      }
      widgetId.current = null;
    };
  }, [action, appearance]);

  return <div ref={box} style={{ margin: '8px 0', minHeight: 0, ...style }} />;
});

export default Turnstile;
